/**
 * Module-level registry that bridges TimelineAssetSettings (which initiates
 * the swap) with EditorPage (which controls the library panel).
 *
 * Flow:
 *  1. TimelineAssetSettings registers a callback and fires `start-asset-swap`.
 *  2. EditorPage opens the correct library in selection mode and shows a
 *     backdrop.
 *  3. When the user picks an asset the library calls onAssetAddedToScene which
 *     EditorPage intercepts, closes swap mode, and invokes the callback.
 *  4. The callback (a closure in TimelineAssetSettings) runs findAssetForSwap
 *     + executeSwap with full access to its local state/services.
 *
 * Pre-swap delete (anchors only):
 *  For an anchor swap whose replacement is uploaded, the old anchor should
 *  disappear the instant the upload starts, not only when executeSwap runs.
 *  TimelineAssetSettings therefore also registers an originalAssetId and a
 *  preSwapDeleteCallback.  The upload wizard calls triggerPreSwapDelete();
 *  subsequent calls are no-ops (idempotent).  A replacement picked from the
 *  library is swapped at once, and executeSimpleSwap deletes the old anchor
 *  inside the swap's undo step unless the upload did.
 */

type SwapCallback = (assetId: string) => void;
type StatusCallback = (isExecuting: boolean) => void;
type PreSwapDeleteCallback = (id: string) => Promise<void>;

let _callback: SwapCallback | null = null;
let _statusCallback: StatusCallback | null = null;
let _originalAssetId: string | null = null;
let _preSwapDeleteCallback: PreSwapDeleteCallback | null = null;
let _preSwapDeleted = false;

export const swapModeRegistry = {
  register(callback: SwapCallback) {
    _callback = callback;
    // Reset pre-swap state so each new swap starts clean
    _originalAssetId = null;
    _preSwapDeleteCallback = null;
    _preSwapDeleted = false;
  },
  invoke(assetId: string) {
    const cb = _callback;
    _callback = null;
    cb?.(assetId);
  },
  isActive() {
    return _callback !== null;
  },
  cancel() {
    _callback = null;
    _originalAssetId = null;
    _preSwapDeleteCallback = null;
    _preSwapDeleted = false;
  },
  /** EditorPage registers this to receive executing-state changes for the overlay. */
  registerStatusCallback(cb: StatusCallback | null) {
    _statusCallback = cb;
  },
  reportStatus(isExecuting: boolean) {
    _statusCallback?.(isExecuting);
  },

  // ── Pre-swap delete (anchor swaps) ──────────────────────────────────────

  /** Store the instance ID of the asset that is about to be replaced. */
  registerOriginalAsset(id: string) {
    _originalAssetId = id;
  },
  /**
   * Store the callback that removes the original asset.
   * Called by TimelineAssetSettings so the closure has access to dispatch /
   * services / sceneAssetsArray at registration time.
   */
  registerPreSwapDeleteCallback(cb: PreSwapDeleteCallback) {
    _preSwapDeleteCallback = cb;
  },
  /**
   * Delete the original asset exactly once per swap session.
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  async triggerPreSwapDelete(): Promise<void> {
    if (_preSwapDeleted || !_preSwapDeleteCallback || !_originalAssetId) return;
    const cb = _preSwapDeleteCallback;
    const id = _originalAssetId;
    // Clear callback immediately to prevent re-entry from nested calls
    _preSwapDeleteCallback = null;
    try {
      await cb(id);
      _preSwapDeleted = true;
    } catch {
      // The removal told the user and put the asset back; the swap deletes it in its own undo step
    }
  },
  /** Returns true after triggerPreSwapDelete() has successfully fired. */
  hasPreSwapDeleted(): boolean {
    return _preSwapDeleted;
  },
};
